import type { Header } from '@tanstack/react-table';
import type { CSSProperties } from 'react';
import { Skeleton } from '@/shared/ui';
import type { PinnedColumnInfo } from '../helpers/types';
import { getPinnedClassName, mergeColumnStyles } from '../helpers/utils';
import { TableCell } from './TableCell';
import { TableRow } from './TableRow';

type TableSkeletonRowsProps<TData> = {
  skeletonRowIds: string[];
  headers: Header<TData, unknown>[];
  columnStyles: Record<string, CSSProperties | undefined>;
  weightedLeafWidthStyles: Record<string, CSSProperties>;
  pinnedColumnStyles?: Record<string, PinnedColumnInfo>;
};

/**
 * ローディング中のスケルトン行をレンダリングするコンポーネント
 */
export const TableSkeletonRows = <TData,>({
  skeletonRowIds,
  headers,
  columnStyles,
  weightedLeafWidthStyles,
  pinnedColumnStyles,
}: TableSkeletonRowsProps<TData>) => {
  return (
    <>
      {skeletonRowIds.map((rowId) => (
        <TableRow key={rowId}>
          {headers
            .filter((header) => !header.isPlaceholder)
            .map((header) => {
              const style = columnStyles[header.column.id];
              const weightedStyle = weightedLeafWidthStyles[header.column.id];
              const mergedStyle = mergeColumnStyles(style, weightedStyle);
              const pinned = pinnedColumnStyles?.[header.column.id];
              const finalStyle = pinned ? { ...mergedStyle, ...pinned.style } : mergedStyle;
              const pinnedDataAttrs = pinned
                ? pinned.side === 'left'
                  ? { 'data-pinned-left': pinned.pinnedOffset }
                  : { 'data-pinned-right': pinned.pinnedOffset }
                : {};
              return (
                <TableCell
                  key={header.id}
                  style={finalStyle}
                  className={getPinnedClassName({ pinned, isCell: true })}
                  {...pinnedDataAttrs}
                >
                  <Skeleton className="h-5 w-full" />
                </TableCell>
              );
            })}
        </TableRow>
      ))}
    </>
  );
};
